import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

function menuItems(menu: HTMLElement | null): HTMLElement[] {
  return [...(menu?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])];
}

/*
 * The "⋯ menu anchored to a row" pattern, extracted from `FolderTree` so the
 * sidebar, the calendar rail and the contacts rail share one implementation
 * rather than three copies that drift. Nothing about the behaviour changed in
 * the move; the comments below are the ones that explain why it is the way it
 * is, kept with the code they explain.
 *
 * Callers supply their own class names, because the menu's *appearance*
 * belongs to the surface it opens on while its *behaviour* belongs here.
 */

/**
 * Open/close for a small anchored menu: a press outside it or Escape closes
 * it. `pointerdown` rather than `mousedown`, so a tap elsewhere in the
 * handheld drawer closes it without waiting for the emulated mouse events a
 * touch browser may or may not send.
 */
export function useAnchoredMenu(): {
  open: boolean;
  setOpen: (update: (previous: boolean) => boolean) => void;
  anchorRef: RefObject<HTMLSpanElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  run: (fn: () => void) => void;
} {
  const [open, setOpenState] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  // The menu is portalled out of the sidebar (see AnchoredMenu), so it is no
  // longer inside the anchor: a click in it has to be recognised on its own or
  // every menu item would close the menu before it ran.
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocument = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpenState(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpenState(false);
    };
    document.addEventListener('pointerdown', onDocument);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocument);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const run = (fn: () => void): void => {
    setOpenState(false);
    fn();
  };
  return { open, setOpen: setOpenState, anchorRef, menuRef, run };
}

/** Gap between the button and its menu, and the margin kept off the edges. */
const MENU_GAP = 4;
const MENU_MARGIN = 8;

/**
 * A menu positioned against its button but rendered at the top of the
 * document.
 *
 * It used to be an absolutely positioned child of the button. The sidebar
 * scrolls (`.sidebar__scroll` is `overflow: auto`), and a scroll container
 * clips its descendants however they are positioned, so on a desktop layout
 * the part of the menu wider than the sidebar was cut off and the message list
 * showed through it -- the menu looked like it was *behind* the list.
 * Portalling to <body> leaves the clip behind; `position: fixed` with the
 * button's viewport rect keeps it in place.
 *
 * It closes on scroll rather than following: the anchor can scroll out of the
 * sidebar entirely, and a menu pinned to nothing is worse than one that shut.
 */
export function AnchoredMenu({
  anchorRef,
  menuRef,
  onDismiss,
  className,
  label,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  onDismiss: () => void;
  className: string;
  label?: string;
  children: ReactNode;
}) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;
    const a = anchor.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    // Right edges aligned, as an absolutely positioned `right: 0` child was.
    let left = a.right - m.width;
    left = Math.min(Math.max(left, MENU_MARGIN), window.innerWidth - m.width - MENU_MARGIN);
    let top = a.bottom + MENU_GAP;
    // Above the button instead when there is no room below it.
    if (top + m.height > window.innerHeight - MENU_MARGIN) {
      top = Math.max(a.top - m.height - MENU_GAP, MENU_MARGIN);
    }
    setPosition({ top, left });
  }, [anchorRef, menuRef]);

  useEffect(() => {
    const dismiss = (): void => onDismiss();
    // `true`: a scroll inside the sidebar does not bubble.
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [onDismiss]);

  /*
   * A menu for the keyboard too (#205, the ARIA menu-button pattern): opening
   * it puts focus on the first item, ↑/↓/Home/End move between items, Tab
   * leaves, and closing it — Escape, a choice, a click outside — hands focus
   * back to the button that opened it. It used to leave focus on the button
   * and take no arrow keys, so Escape went on to the page behind (in Mail:
   * closing the open message) and focus fell to <body>.
   */
  const placed = position !== null;
  useEffect(() => {
    if (!placed) return;
    const first = menuItems(menuRef.current)[0];
    if (first && !menuRef.current?.contains(document.activeElement)) first.focus({ preventScroll: true });
  }, [placed, menuRef]);
  useEffect(() => {
    const anchor = anchorRef.current;
    const trigger = anchor?.querySelector<HTMLElement>('button, [role="button"]') ?? null;
    const menu = menuRef.current;
    return () => {
      const active = document.activeElement;
      const lost = active === null || active === document.body || (menu?.contains(active) ?? false);
      if (lost && trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [anchorRef, menuRef]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const list = menuItems(menuRef.current);
    if (list.length === 0) return;
    const at = list.indexOf(document.activeElement as HTMLElement);
    const go = (index: number) => {
      event.preventDefault();
      list[(index + list.length) % list.length]?.focus();
    };
    if (event.key === 'ArrowDown') go(at + 1);
    else if (event.key === 'ArrowUp') go(at < 0 ? list.length - 1 : at - 1);
    else if (event.key === 'Home') go(0);
    else if (event.key === 'End') go(list.length - 1);
    else if (event.key === 'Tab') {
      event.preventDefault();
      onDismiss();
    }
  };

  return createPortal(
    // The keys belong to the menu (the menu-button pattern); its items are
    // buttons and take Enter and Space themselves.
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      ref={menuRef}
      className={className}
      role="menu"
      onKeyDown={onKeyDown}
      {...(label ? { 'aria-label': label } : {})}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        // Measured before it is placed; painting it at 0,0 for that one frame
        // would be a flash in the corner of the screen.
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

